import React, { useState, useEffect, useMemo } from 'react';
import {
  Select, MenuItem, FormControl, InputLabel, CircularProgress, TextField
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AutoAwesome as SparklesIcon,
  Collections as GalleryIcon,
  CheckCircle as CheckIcon,
  Folder as FolderIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import AppHeader from '../components/common/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import GalleryModal, { GalleryItem } from '../components/stylista/GalleryModal';

// #T# HairdresserPage — Hairdresser Studio
// Galeria twarzy:    /stylista/faces/woman/female-faces.json (status === 'done')
// Galeria fryzur:    /stylista/hairdresser/female/female-hairdresser.json
// Backend:           POST /api/hairdresser/generate — faceImage (file) + prompt + model + resolution + aspect_ratio

interface HairstyleItem {
  name: string;
  path: string;
  category: { gender: string; type: string; subtype: string; hair_color: string };
  prompt: string;
  status: string;
}

interface FaceJson {
  name: string;
  path: string;
  category: { gender: string; age: string; ethnicity: string; hair_color: string };
  status: string;
}

const MODELS = [
  { value: 'strawberry-pro2', label: 'Strawberry Pro 2 (Flash)' },
  { value: 'strawberry-pro', label: 'Strawberry Pro (4K)' },
  { value: 'blueberry-45', label: 'Blueberry 4.5' },
  { value: 'lemon-lux', label: 'Lemon Lux' },
];

const HAIR_COLORS = ['blonde', 'brunette', 'black', 'red', 'grey'];

type GalleryType = 'faces' | 'hairstyles' | null;

const HairdresserPage: React.FC = () => {
  const { isAdmin } = useAuth();

  // ---- Inputs ----
  const [faceFile, setFaceFile] = useState<File | null>(null);
  const [facePreview, setFacePreview] = useState<string | null>(null);
  const [selectedHairstyle, setSelectedHairstyle] = useState<HairstyleItem | null>(null);
  const [selectedModel, setSelectedModel] = useState('strawberry-pro2');
  const [selectedColor, setSelectedColor] = useState<string>('brunette');
  const [promptOverride, setPromptOverride] = useState('');

  // ---- Gallery ----
  const [galleryFaces, setGalleryFaces] = useState<GalleryItem[]>([]);
  const [galleryHairstyles, setGalleryHairstyles] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState<Record<string, boolean>>({});
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const [galleryType, setGalleryType] = useState<GalleryType>(null);

  // Raw hairstyle data for resolving selection from gallery
  const [hairstylesRaw, setHairstylesRaw] = useState<HairstyleItem[]>([]);

  // ---- Generation ----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  // ---- Face preview from File ----
  useEffect(() => {
    if (!faceFile) { setFacePreview(null); return; }
    const url = URL.createObjectURL(faceFile);
    setFacePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [faceFile]);

  // ---- Load galleries (lazy) ----
  const loadGallery = async (kind: 'faces' | 'hairstyles') => {
    if (galleryLoading[kind]) return;
    setGalleryLoading(p => ({ ...p, [kind]: true }));
    setGalleryError(null);
    try {
      if (kind === 'faces') {
        const [femaleResp, maleResp] = await Promise.all([
          fetch('/stylista/faces/woman/female-faces.json'),
          fetch('/stylista/faces/man/male-faces.json'),
        ]);
        const [femaleData, maleData] = await Promise.all([femaleResp.json(), maleResp.json()]);
        const allFaces: FaceJson[] = [
          ...(Array.isArray(femaleData) ? femaleData : []),
          ...(Array.isArray(maleData) ? maleData : []),
        ].filter(f => f.status === 'done');
        setGalleryFaces(allFaces.map(f => ({
          id: f.name,
          name: f.name.replace(/_/g, ' '),
          thumbnail_url: `/stylista/faces/${f.path}/${f.name}_strawberry_pro_miniature.jpg`,
          image_url: `/stylista/faces/${f.path}/${f.name}_strawberry_pro.jpg`,
          group: f.category.ethnicity,
          gender: f.category.gender,
          age: f.category.age,
          hair_color: f.category.hair_color,
        })));
      } else {
        const [femaleResp, maleResp] = await Promise.all([
          fetch('/stylista/hairdresser/female/female-hairdresser.json'),
          fetch('/stylista/hairdresser/male/male-hairdresser.json'),
        ]);
        const [femaleData, maleData] = await Promise.all([femaleResp.json(), maleResp.json()]);
        const allItems: HairstyleItem[] = [
          ...(Array.isArray(femaleData) ? femaleData : []),
          ...(Array.isArray(maleData) ? maleData : []),
        ].filter(s => s.status !== 'pending');
        setHairstylesRaw(allItems);
        setGalleryHairstyles(allItems.map(s => ({
          id: s.name,
          name: `${s.category.type.charAt(0).toUpperCase() + s.category.type.slice(1)} - ${s.category.hair_color.charAt(0).toUpperCase() + s.category.hair_color.slice(1)}`,
          thumbnail_url: `/stylista/hairdresser/${s.path}/${s.name}_strawberry_pro_miniature.jpg`,
          image_url: `/stylista/hairdresser/${s.path}/${s.name}_strawberry_pro.jpg`,
          group: s.category.type,
          gender: s.category.gender,
          hair_color: s.category.hair_color,
        })));
      }
    } catch {
      setGalleryError('Failed to load gallery.');
    } finally {
      setGalleryLoading(p => ({ ...p, [kind]: false }));
    }
  };

  const openGallery = (type: GalleryType) => {
    if (type) {
      const notLoaded = type === 'faces' ? galleryFaces.length === 0 : galleryHairstyles.length === 0;
      if (notLoaded && !galleryLoading[type]) loadGallery(type);
    }
    setGalleryType(type);
  };

  const handleGallerySelect = async (item: GalleryItem) => {
    if (galleryType === 'faces') {
      const src = item.image_url || '';
      if (!src) { setGalleryError('No image URL.'); return; }
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const safeName = (item.name || 'face').replace(/\s+/g, '-');
        setFaceFile(new File([blob], `${safeName}.jpg`, { type: 'image/jpeg' }));
      } catch (e: any) {
        setGalleryError(`Cannot fetch image: ${e.message}`);
      }
    } else if (galleryType === 'hairstyles') {
      const style = hairstylesRaw.find(s => s.name === item.id);
      if (style) {
        setSelectedHairstyle(style);
        setSelectedColor(style.category.hair_color);
      }
    }
    setGalleryType(null);
  };

  // ---- Effective prompt for admin ----
  const effectivePrompt = useMemo(() => {
    if (promptOverride.trim()) return promptOverride.trim();
    if (!selectedHairstyle) return '';
    return selectedHairstyle.prompt || '';
  }, [promptOverride, selectedHairstyle]);

  const currentFaceDisplay = facePreview;

  // ---- Generate ----
  const handleGenerate = async () => {
    if (!selectedHairstyle) { setError('Please select a hairstyle'); return; }
    if (!faceFile) { setError('Please upload a face photo or select from gallery'); return; }

    setLoading(true); setError(null); setResult(null);
    try {
      const formData = new FormData();
      formData.append('faceImage', faceFile);
      formData.append('prompt', effectivePrompt || selectedHairstyle.prompt);
      formData.append('model', selectedModel);
      formData.append('resolution', '2k');
      formData.append('aspect_ratio', '3:4');

      const response = await apiClient.post('hairdresser/generate', formData, {
        headers: { 'Content-Type': null },
        timeout: 180000,
      });

      if (response.data.success) setResult(response.data);
      else setError(response.data.message || 'Generation failed');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const clearFace = () => setFaceFile(null);

  const hairstyleCount = galleryHairstyles.length > 0 ? galleryHairstyles.length : '...';

  return (
    <>
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">💇 Hairdresser Studio</h1>
          <p className="text-sm text-gray-500 mt-1">Choose a hairstyle and see it on your face</p>
        </div>

        {/* Error / gallery error */}
        {error && (
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-600"><CloseIcon fontSize="small" /></button>
          </div>
        )}
        {galleryError && (
          <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 mb-4 text-sm text-yellow-700">
            <span>{galleryError}</span>
            <button onClick={() => setGalleryError(null)} className="ml-3 text-yellow-400 hover:text-yellow-600"><CloseIcon fontSize="small" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Inputs */}
          <div className="flex flex-col gap-4">

            {/* Step 1: Face */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-700 mb-3">1. Your Face</h2>
              {currentFaceDisplay ? (
                <div className="relative">
                  <img
                    src={currentFaceDisplay} alt="Selected face"
                    className="w-full max-h-72 object-contain rounded-lg border-2 border-green-400"
                  />
                  <button
                    onClick={clearFace}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-colors min-h-[44px]">
                      <UploadIcon fontSize="small" />
                      Upload Your Photo
                      <input type="file" className="hidden" accept="image/*"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setFaceFile(f); }} />
                    </label>
                    <label className="flex items-center justify-center gap-1 px-4 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-colors min-h-[44px]">
                      <CameraIcon fontSize="small" />
                      <input type="file" className="hidden" accept="image/*" capture="user"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setFaceFile(f); }} />
                    </label>
                  </div>
                  <span className="text-xs text-gray-400 text-center">lub</span>
                  <button
                    onClick={() => openGallery('faces')}
                    className="flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors min-h-[44px]"
                  >
                    <GalleryIcon fontSize="small" />
                    Galeria twarzy ({galleryFaces.length > 0 ? galleryFaces.length : '...'})
                  </button>
                </div>
              )}
            </div>

            {/* Step 2: Hair Color */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-700 mb-3">2. Hair Color</h2>
              <div className="flex gap-1 flex-wrap">
                {HAIR_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors min-h-[36px] ${
                      selectedColor === color
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                    }`}
                  >
                    {color.charAt(0).toUpperCase() + color.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Hairstyle */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-700 mb-3">3. Hairstyle</h2>
              {selectedHairstyle ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <img
                    src={`/stylista/hairdresser/${selectedHairstyle.path}/${selectedHairstyle.name}_strawberry_pro.jpg`}
                    alt={selectedHairstyle.name}
                    className="w-full h-48 object-contain bg-gray-50"
                  />
                  <div className="px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">
                        {selectedHairstyle.category.type.charAt(0).toUpperCase() + selectedHairstyle.category.type.slice(1)}
                        {' - '}
                        {selectedHairstyle.category.hair_color.charAt(0).toUpperCase() + selectedHairstyle.category.hair_color.slice(1)}
                      </p>
                      <span className="inline-block text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 mt-1">
                        {selectedHairstyle.category.subtype}
                      </span>
                    </div>
                    <button onClick={() => setSelectedHairstyle(null)} className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => openGallery('hairstyles')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors min-h-[44px]"
                >
                  <FolderIcon fontSize="small" />
                  Browse Hairstyles ({hairstyleCount})
                </button>
              )}
            </div>

            {/* Step 4: Model (admin only) */}
            {isAdmin && (
              <div className="border border-gray-200 rounded-xl p-4">
                <h2 className="font-semibold text-gray-700 mb-3">4. AI Model</h2>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Model</InputLabel>
                  <Select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} label="Select Model">
                    {MODELS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Admin: reference preview */}
            {isAdmin && currentFaceDisplay && (
              <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Reference images (admin)</p>
                <div className="flex gap-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Image 1: Face</p>
                    <img src={currentFaceDisplay} alt="face ref" className="h-20 w-20 object-cover rounded border border-gray-200" />
                  </div>
                </div>
              </div>
            )}

            {/* Admin: prompt override */}
            {isAdmin && (
              <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Prompt (admin)</p>
                <TextField
                  fullWidth multiline minRows={4}
                  value={promptOverride !== '' ? promptOverride : effectivePrompt}
                  onChange={(e) => setPromptOverride(e.target.value)}
                  placeholder="Prompt from selected hairstyle (editable)"
                  helperText="Auto-filled from selected hairstyle. Edit to override."
                  sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                />
              </div>
            )}

            {/* Generate */}
            <div className="sticky bottom-0 z-20 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-t border-gray-100 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0">
              <button
                onClick={handleGenerate}
                disabled={loading || !selectedHairstyle || !faceFile}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors min-h-[52px]"
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <SparklesIcon fontSize="small" />}
                {loading ? 'Generating...' : 'Generate Hairstyle'}
              </button>
            </div>
          </div>

          {/* Right: Result */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h2 className="font-semibold text-gray-700 mb-3">Result</h2>
            {result ? (
              <div>
                {result.preview_files?.map((file: string, idx: number) => (
                  <img key={idx} src={file} alt={`Result ${idx + 1}`}
                    className="w-full rounded-xl mb-3 border-2 border-green-400" />
                ))}
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700">
                  <CheckIcon fontSize="small" />
                  <span>{result.message}</span>
                </div>
                {isAdmin && result.metadata?.stylista_prompt && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">Prompt sent to model:</p>
                    <pre className="text-xs font-mono bg-blue-50/40 border border-blue-100 rounded p-2 whitespace-pre-wrap break-words text-gray-600">
                      {result.metadata.stylista_prompt}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-sm text-gray-400">Your result will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* GalleryModal — faces */}
      <GalleryModal
        open={galleryType === 'faces'}
        onClose={() => setGalleryType(null)}
        onSelect={handleGallerySelect}
        items={galleryFaces}
        loading={!!galleryLoading['faces']}
        title="Galeria twarzy"
        defaultGroup="european"
        defaultGender="woman"
        defaultAge="20plus"
        groupLabel="Pochodzenie:"
      />

      {/* GalleryModal — hairstyles (portrait 3:4, contain) */}
      <GalleryModal
        open={galleryType === 'hairstyles'}
        onClose={() => setGalleryType(null)}
        onSelect={handleGallerySelect}
        items={galleryHairstyles}
        loading={!!galleryLoading['hairstyles']}
        title="Galeria fryzur"
        defaultGender="female"
        groupLabel="Typ:"
        imageObjectFit="contain"
        imageHeight={180}
      />
    </>
  );
};

export default HairdresserPage;
